import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BomPartType } from 'bom/enums';
import { Button } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { QuoteOrderPricingType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IQuotePart, IVendorQuote } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { SubmitVendorOrderConfirmation } from './SubmitVendorOrderConfirmation';

interface IProps {
  quote: IVendorQuote;
}

export const SubmitVendorOrderButton = ({ quote }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { rows, isDataLoadCompleted } = useAgGridClientTableContext();

  const { submitVendorOrder, isSubmitVendorOrderLoading } = useQuoteActions();

  const activeParts = getActiveQuoteParts(rows as IQuotePart[]);

  const isSubmitOrderEnabled =
    isDataLoadCompleted &&
    !isSubmitVendorOrderLoading &&
    activeParts?.some((part: IQuotePart) => BomPartType.PART === part.type);

  const onSubmitOrder = async (submitOrderPricingType = QuoteOrderPricingType.INCLUDE) => {
    setIsConfirmationOpen(false);

    const { success } = await submitVendorOrder({
      quote: { id: quote.id },
      omitNoPricing: QuoteOrderPricingType.OMIT === submitOrderPricingType ? 1 : 0,
    });

    if (!success) {
      return;
    }
  };

  return (
    <>
      <Button
        text={t('buttonLabelSubmitOrder')}
        variant={!isSubmitOrderEnabled ? Variant.OUTLINED : Variant.SOLID}
        color={Color.SUCCESS}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={!isSubmitOrderEnabled}
      />

      {isConfirmationOpen && (
        <SubmitVendorOrderConfirmation
          activeParts={activeParts}
          onConfirm={onSubmitOrder}
          onCancel={() => setIsConfirmationOpen(false)}
        />
      )}
    </>
  );
};
